/* Greenleaf bundle widget. Display overrides never alter Shopify checkout prices. */
(function (root) {
  'use strict';
  const id = value => String(value ?? '').trim();
  function override(value) {
    const text = id(value);
    if (!text) return null;
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(text)) return NaN;
    return Math.round(Number(text.replace(',', '.')) * 100);
  }
  function variantFor(tier) {
    const variants = tier?.variants || [];
    if (id(tier?.variantId)) return variants.find(v => id(v.id) === id(tier.variantId)) || null;
    if (id(tier?.variantTitle)) return variants.find(v => v.title === id(tier.variantTitle)) || null;
    return variants.find(v => v.available) || variants[0] || null;
  }
  function planFor(tier, variant, config) {
    const plans = variant?.plans || [], requested = id(tier.planId) || id(config.planId);
    if (requested) return plans.find(p => id(p.id) === requested) || null;
    return plans.length === 1 ? plans[0] : null;
  }
  function pricing(tier, variant, config, subscribed) {
    if (!variant) return null;
    const saleOverride = override(tier.priceOverride), compareOverride = override(tier.compareOverride);
    const oneTime = saleOverride ?? variant.price * tier.quantity;
    const compareAt = compareOverride ?? (variant.compareAt ? variant.compareAt * tier.quantity : null);
    const plan = planFor(tier, variant, config);
    // Shopify's allocation price belongs to this variant and is per cart unit.
    // Display overrides and legacy percentage settings never set subscription prices.
    const subscription = config.subscriptionEnabled && Number.isSafeInteger(plan?.price) && plan.price >= 0
      ? plan.price * tier.quantity : NaN;
    const total = subscribed ? subscription : oneTime;
    return { oneTime, total, compareAt, saving: compareAt > total ? compareAt - total : 0 };
  }
  function selection(config, index, subscribed) {
    const tier = config.tiers[index], variant = variantFor(tier);
    if (!tier || !variant) return { error: 'This option is currently unavailable. Please choose another supply.' };
    if (!variant.available) return { error: 'This option is sold out. Please choose another supply.' };
    if (!/^\d+$/.test(id(variant.id)) || !Number.isSafeInteger(Number(variant.id)) || Number(variant.id) <= 0) return { error: 'This option is currently unavailable.' };
    if (!Number.isSafeInteger(tier.quantity) || tier.quantity < 1) return { error: 'This option is currently unavailable.' };
    if (!subscribed && tier.requiresPlan) return { error: 'This option is available with automatic refills. Please select the subscription option.' };
    const item = { id: Number(variant.id), quantity: tier.quantity };
    let plan = null;
    if (subscribed) {
      plan = planFor(tier, variant, config);
      if (!config.subscriptionEnabled || !plan || !/^\d+$/.test(id(plan.id)) || !Number.isSafeInteger(Number(plan.id)) || Number(plan.id) <= 0) return { error: 'Automatic refills are unavailable for this option. Choose a one-time purchase or another supply.' };
      item.selling_plan = Number(plan.id);
    }
    const prices = pricing(tier, variant, config, subscribed);
    if (!Number.isSafeInteger(prices.total) || prices.total < 0 || (prices.compareAt !== null && !Number.isFinite(prices.compareAt))) return { error: 'Pricing is currently unavailable for this option.' };
    return { tier, variant, plan, prices, payload: { items: [item] } };
  }
  const core = { override, variantFor, planFor, pricing, selection };
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (!root.document || root.GreenleafBundle) return;
  const instances = new Map();
  function mount(el) {
    let config;
    try { config = JSON.parse(el.querySelector('[data-gl-product-config]').textContent); } catch (_) { return; }
    const controller = new AbortController(), observers = [], signal = controller.signal;
    const on = (node, type, fn, options = {}) => node?.addEventListener(type, fn, { ...options, signal });
    const form = el.querySelector('[data-gl-bundle-form]'), fieldset = form.querySelector('fieldset');
    const cards = [...el.querySelectorAll('[data-gl-tier]')], subscribe = el.querySelector('[data-gl-subscribe]');
    const discountBadge = el.querySelector('[data-gl-subscription-discount]');
    if (subscribe) subscribe.checked = !!config.subscriptionEnabled;
    const button = el.querySelector('[data-gl-add]'), buttonLabel = el.querySelector('[data-gl-add-label]');
    const status = el.querySelector('[data-gl-cart-status]'), selectionError = el.querySelector('[data-gl-selection-error]');
    let selected = Math.max(0, Math.min(config.tiers.length - 1, config.defaultTier || 0)), pending = false, navigating = false, restoreCartBehavior = null;
    if (!variantFor(config.tiers[selected])?.available) {
      const available = config.tiers.findIndex(t => variantFor(t)?.available);
      if (available >= 0) selected = available;
    }
    let formatter;
    try { formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: config.currency }); }
    catch (_) { formatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR' }); }
    const money = value => formatter.format(value / 100);
    function render() {
      const subscribed = !!subscribe?.checked;
      cards.forEach((card, i) => {
        const tier = config.tiers[i], variant = variantFor(tier), prices = pricing(tier, variant, config, subscribed);
        const radio = card.querySelector('input'); radio.checked = selected === i; radio.disabled = !variant?.available;
        card.classList.toggle('is-selected', selected === i); card.classList.toggle('is-unavailable', !variant?.available);
        const priceEl = card.querySelector('[data-gl-tier-price]'), compare = card.querySelector('[data-gl-tier-compare]'), saving = card.querySelector('[data-gl-tier-saving]');
        priceEl.textContent = !variant ? 'Unavailable' : !variant.available ? 'Sold out' : prices && Number.isFinite(prices.total) ? money(prices.total) : '—';
        compare.hidden = !(prices?.compareAt > prices?.total && variant?.available);
        compare.textContent = compare.hidden ? '' : money(prices.compareAt);
        saving.textContent = !compare.hidden ? `${saving.dataset.glSavingsLabel || 'Save'} ${money(prices.saving)}` : '';
        const image = card.querySelector('[data-gl-tier-image]');
        if (image && variant?.image && !tier.customImage) image.src = variant.image;
      });
      const state = selection(config, selected, subscribed);
      if (discountBadge) {
        const subscriptionState = selection(config, selected, true);
        const prices = subscriptionState.prices;
        const percent = prices && prices.oneTime > prices.total
          ? (prices.oneTime - prices.total) / prices.oneTime : 0;
        discountBadge.hidden = !Number.isFinite(percent) || percent <= 0;
        discountBadge.textContent = discountBadge.hidden ? '' : '−' + new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'percent', maximumFractionDigits: 2 }).format(percent);
      }
      fieldset.disabled = pending; if (subscribe) subscribe.disabled = pending;
      button.disabled = pending || !!state.error; button.setAttribute('aria-busy', String(pending));
      buttonLabel.textContent = pending ? 'Adding…' : config.buttonLabel;
      selectionError.textContent = state.error || '';
      selectionError.hidden = !state.error;
    }
    on(form, 'change', e => {
      const radio = e.target.closest('[data-gl-tier] input');
      if (radio) selected = Number(radio.value);
      status.textContent = ''; el.querySelector('[data-gl-view-cart]').hidden = true; render();
    });
    function suppressCartAutoOpen() {
      // UpCart's documented integration controls. Apply only during this purchase.
      const overrides = {
        upcartOpenOnAddToCartOverride: false,
        upcartShouldOpenOnAddToCartBackgroundNetworkCall: false,
        upcartShouldSkipAddToCartInterceptor: true
      };
      const previous = Object.keys(overrides).map(key => ({
        key, exists: Object.prototype.hasOwnProperty.call(root, key), value: root[key]
      }));
      Object.assign(root, overrides);
      return () => previous.forEach(({ key, exists, value }) => {
        if (root[key] !== overrides[key]) return;
        if (exists) root[key] = value; else delete root[key];
      });
    }
    async function goToCheckout() {
      if (pending) return;
      const state = selection(config, selected, !!subscribe?.checked);
      if (state.error) { status.textContent = state.error; return; }
      pending = true; status.textContent = ''; render();
      const cartRoot = (root.Shopify?.routes?.root || config.cartRoot || '/').replace(/\/?$/, '/');
      try {
        restoreCartBehavior = suppressCartAutoOpen();
        const response = await fetch(cartRoot + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(state.payload)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Could not add this option. Please check availability and try again.');
        // Explicit navigation: do not rely on /cart/add form return_to handling.
        // Keep UpCart suppressed until the page unloads, including delayed callbacks.
        root.location.assign(cartRoot + 'checkout');
        navigating = true;
      } catch (error) {
        status.textContent = error instanceof TypeError ? 'We could not confirm the cart update. Check your cart before trying again.' : error.message || 'Could not open checkout. Please try again.';
      } finally {
        if (!navigating) {
          restoreCartBehavior?.(); restoreCartBehavior = null; pending = false;
          if (el.isConnected) render();
        }
      }
    }
    // Intercept only this widget's purchase button, before document-level cart handlers.
    button.type = 'button';
    on(root, 'click', e => {
      if (e.target !== button && !button.contains(e.target)) return;
      e.preventDefault(); e.stopImmediatePropagation();
      if (!button.disabled) goToCheckout();
    }, { capture: true });
    // Preserve keyboard/form submission without exposing it to drawer handlers.
    on(root, 'submit', e => {
      if (e.target !== form) return;
      e.preventDefault(); e.stopImmediatePropagation();
      goToCheckout();
    }, { capture: true });
    // Restore the widget if the shopper returns with the browser Back button.
    on(root, 'pageshow', e => {
      if (!e.persisted) return;
      restoreCartBehavior?.(); restoreCartBehavior = null; pending = false; navigating = false;
      if (el.isConnected) render();
    });
    const track = el.querySelector('[data-gl-gallery-track]'), slides = [...el.querySelectorAll('[data-gl-gallery-slide]')], thumbs = [...el.querySelectorAll('[data-gl-thumb]')];
    const prev = el.querySelector('[data-gl-gallery-prev]'), next = el.querySelector('[data-gl-gallery-next]');
    let imageIndex = 0;
    function markImage(i) {
      imageIndex = Math.max(0, Math.min(slides.length - 1, i));
      thumbs.forEach((thumb, n) => thumb.setAttribute('aria-pressed', String(n === imageIndex)));
      if (prev) prev.disabled = imageIndex === 0; if (next) next.disabled = imageIndex === slides.length - 1;
      const active = thumbs[imageIndex], holder = active?.parentElement;
      if (holder) holder.scrollTo({ left: active.offsetLeft - holder.offsetLeft - holder.clientWidth / 2 + active.clientWidth / 2, behavior: 'auto' });
    }
    function showImage(i) {
      markImage(i); if (!slides[imageIndex]) return;
      const behavior = root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      track.scrollTo({ left: slides[imageIndex].offsetLeft - slides[0].offsetLeft, behavior });
    }
    thumbs.forEach((thumb, i) => on(thumb, 'click', () => showImage(i)));
    on(prev, 'click', () => showImage(imageIndex - 1)); on(next, 'click', () => showImage(imageIndex + 1));
    on(track, 'keydown', e => { if (['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); showImage(imageIndex + (e.key === 'ArrowRight' ? 1 : -1)); } });
    if ('IntersectionObserver' in root && slides.length) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.intersectionRatio > .6) markImage(slides.indexOf(entry.target)); }), { root: track, threshold: [.6] });
      slides.forEach(slide => observer.observe(slide)); observers.push(observer);
    }
    on(el, 'shopify:block:select', e => {
      const card = e.target.closest('[data-gl-tier]');
      if (card && !pending) { selected = Number(card.dataset.glTier); render(); }
    });
    markImage(0); render();
    instances.set(el, () => { controller.abort(); observers.forEach(o => o.disconnect()); });
  }
  function refresh() {
    for (const [el, cleanup] of instances) if (!el.isConnected) { cleanup(); instances.delete(el); }
    document.querySelectorAll('[data-gl-product-section]').forEach(el => { if (!instances.has(el)) mount(el); });
    root.GreenleafListicle?.refresh();
  }
  root.GreenleafBundle = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true }); else refresh();
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(type => document.addEventListener(type, refresh));
})(typeof window !== 'undefined' ? window : globalThis);
